/**
 * Attendee-aware pantry deduction when a meal is cooked (competitor-
 * prioritization rank #2: "Pantry-aware recipe and meal suggestions", the
 * Phase-3 "Menu/Koken full loop → stock deduction/suggestion when cooked"
 * slice — the named remaining half after `weeklyGroceryPlan`).
 *
 * ## Why this exists — a correctness gap, not just a feature
 *
 * `handlePlanCook` (the offline worker) already decrements the pantry per linked
 * ingredient, but it deducts the *raw planned* amount (`neededFromStock ||
 * quantity`) regardless of who actually ate. A dinner sized for 4 but eaten by 2
 * over-drains the pantry by half; a plan for 2 that 5 people crash under-drains
 * it. Either way the live inventory — Clando's entire moat over AnyList/Bring!/
 * OurGroceries, none of which hold stock — silently drifts away from reality.
 *
 * The fix: scale each ingredient's deduction by **actual attendance** relative
 * to the servings the plan was sized for. `MealPlanIngredient` already carries
 * `neededFromStock`; this module turns that planned amount into the amount to
 * deduct *given who showed up*, and surfaces the on-hand / remaining / shortfall
 * so the confirm dialog can show the real effect before writing.
 *
 * Pure, DB-free, no clock, no strings (mirrors `weeklyGroceryPlan` /
 * `familyDigest` / `cookNow`: one scoring brain, the view localises). The DB
 * write that applies the plan stays in `MealEventHandler.handlePlanCook`, kept
 * out of here so the math is unit-provable without a Supabase mock.
 */

import type { MealPlan, MealPlanIngredient } from '../types/meal.js';
import type { StockItem } from '../types/stock.js';

/** One stock-linked ingredient's deduction after attendance scaling. */
export interface CookDeductionLine {
  stockItemId: string;
  name: string;
  unit: string;
  /** Amount the plan was sized to consume (`neededFromStock || quantity`). */
  plannedQuantity: number;
  /** `plannedQuantity * scaleFactor`, rounded — the amount to actually deduct. */
  deductQuantity: number;
  /** On-hand before deduction (`currentQuantity + activeQuantity`). */
  onHand: number;
  /** Pantry level after the deduction, clamped at 0. */
  remaining: number;
  /** `max(0, deductQuantity - onHand)` — the part that can't be covered. */
  shortfall: number;
  /**
   * True when this line was held flat (deducted at planned amount regardless of
   * attendance) because it's a "don't scale this" ingredient — a whole onion, a
   * tin, a pinch of salt. The dialog can mark it so the cook understands why a
   * line didn't shrink with the party.
   */
  heldFlat: boolean;
}

export interface CookDeductionPlan {
  /** Stock-linked, non-optional lines with a positive deduction, name-sorted. */
  lines: CookDeductionLine[];
  /** Servings the plan was sized for. */
  plannedServings: number;
  /** Servings derived from attendance, else `plannedServings`. */
  effectiveServings: number;
  /** `effectiveServings / plannedServings`, clamped to `[0, maxScale]`. */
  scaleFactor: number;
  /** True when scaling actually changed the deducted amounts. */
  scaled: boolean;
  /** Sum of `shortfall` — pantry items that would go negative. */
  totalShortfall: number;
}

export interface CookDeductionOptions {
  /** Scale deductions by attendance. Default true. When false, deduct planned. */
  scaleByAttendance?: boolean;
  /** Count `maybe` RSVPs as half a head. Default false (only firm `attending`). */
  countMaybeAsHalf?: boolean;
  /**
   * Upper bound on the scale factor so a surprise crowd can't drain the pantry
   * unboundedly past what was planned. Default 2.
   */
  maxScale?: number;
  /**
   * Round deductions up to whole units (for discrete things like eggs you can't
   * use a fraction). Default false — round to 2 decimals so grams/ml stay exact.
   */
  roundUpToWhole?: boolean;
  /**
   * Stock item ids to hold flat (deduct planned amount, never scaled by
   * attendance), in addition to ingredients whose own `noScale` flag is set.
   * Lets the cook dialog toggle "don't scale this" per ingredient without a DB
   * column — the override travels with the UI, keeping this module DB-free.
   */
  noScaleStockItemIds?: string[];
}

/** Round to at most 2 decimals, killing float noise (0.1+0.2 → 0.3). */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Effective head count from a plan's RSVPs. Only firm `attending` count as a
 * full head; `maybe` is half a head when `countMaybeAsHalf`. Returns null when
 * no one has RSVP'd at all (caller falls back to planned servings — absence of
 * data must never be read as "nobody is eating").
 */
function attendingHeads(plan: MealPlan, countMaybeAsHalf: boolean): number | null {
  const attendees = plan.attendees || [];
  if (!attendees.length) return null;
  let heads = 0;
  let sawResponse = false;
  for (const a of attendees) {
    if (a.status === 'attending') {
      heads += 1;
      sawResponse = true;
    } else if (a.status === 'maybe') {
      if (countMaybeAsHalf) heads += 0.5;
      sawResponse = true;
    } else if (a.status === 'not_attending') {
      sawResponse = true;
    }
  }
  // Everyone declined is meaningful data (heads 0); no firm signal is not.
  return sawResponse ? heads : null;
}

/**
 * Build the attendee-scaled deduction plan for a meal about to be marked cooked.
 *
 * Scaling: `effectiveServings = attending heads` (when anyone RSVP'd and scaling
 * is on), else the planned servings. `scaleFactor = effectiveServings /
 * plannedServings`, clamped to `[0, maxScale]`. Each stock-linked, non-optional
 * ingredient's planned amount (`neededFromStock || quantity`) is multiplied by
 * the factor and rounded. Freeform and optional ingredients never touch stock,
 * matching the existing cook path and `computeShortfalls`.
 *
 * "Don't scale this" overrides: an ingredient flagged `noScale` (or whose stock
 * id is in `options.noScaleStockItemIds`) is held flat — a whole onion, a tin, a
 * pinch of salt is one unit whether 2 or 6 eat. It still respects whether the
 * meal happened at all: if attendance collapses the factor to 0 (everyone
 * declined), even a held-flat line deducts nothing.
 */
export function composeCookDeduction(
  plan: MealPlan,
  stockItems: StockItem[],
  options: CookDeductionOptions = {},
): CookDeductionPlan {
  const scaleByAttendance = options.scaleByAttendance ?? true;
  const countMaybeAsHalf = options.countMaybeAsHalf ?? false;
  const maxScale = options.maxScale ?? 2;
  const roundUpToWhole = options.roundUpToWhole ?? false;
  const noScaleIds = new Set(options.noScaleStockItemIds ?? []);

  const plannedServings = plan.servings > 0 ? plan.servings : 0;

  const heads = scaleByAttendance ? attendingHeads(plan, countMaybeAsHalf) : null;
  const effectiveServings = heads != null ? heads : plannedServings;

  // Only scale when we have a usable planned baseline AND real attendance data.
  let scaleFactor = 1;
  if (scaleByAttendance && heads != null && plannedServings > 0) {
    scaleFactor = Math.max(0, Math.min(maxScale, effectiveServings / plannedServings));
  }
  const scaled = scaleFactor !== 1;

  const byId = new Map(stockItems.map((s) => [s.id, s]));
  const lines: CookDeductionLine[] = [];

  for (const ing of (plan.ingredients || []) as MealPlanIngredient[]) {
    if (!ing.stockItemId || ing.optional) continue;
    const planned = ing.neededFromStock > 0 ? ing.neededFromStock : ing.quantity || 0;
    if (planned <= 0) continue;

    // Held-flat ingredients ignore the proportional factor but still gate on
    // whether the meal happened at all (factor 0 = everyone declined → nothing).
    const heldFlat = ing.noScale === true || noScaleIds.has(ing.stockItemId);
    const lineFactor = heldFlat ? (scaleFactor > 0 ? 1 : 0) : scaleFactor;

    let deduct = planned * lineFactor;
    deduct = roundUpToWhole ? Math.ceil(deduct) : round2(deduct);
    if (deduct <= 0) continue;

    const item = byId.get(ing.stockItemId);
    const onHand = item ? (item.currentQuantity ?? 0) + (item.activeQuantity ?? 0) : 0;
    const name = item?.name || ing.freeformName || '—';

    lines.push({
      stockItemId: ing.stockItemId,
      name,
      unit: ing.unit || item?.unit || '',
      plannedQuantity: round2(planned),
      deductQuantity: deduct,
      onHand: round2(onHand),
      remaining: round2(Math.max(0, onHand - deduct)),
      shortfall: round2(Math.max(0, deduct - onHand)),
      heldFlat,
    });
  }

  lines.sort((a, b) => a.name.localeCompare(b.name));
  const totalShortfall = round2(lines.reduce((sum, l) => sum + l.shortfall, 0));

  return {
    lines,
    plannedServings,
    effectiveServings: round2(effectiveServings),
    scaleFactor: round2(scaleFactor),
    scaled,
    totalShortfall,
  };
}
